import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PartyPopper, Hourglass, XCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

const COLORS = ["#FF80AB", "#FFE082", "#A8E6CF", "#80D8FF", "#E1BEE7"];

function Confetti() {
  const pieces = Array.from({ length: 40 }, (_, i) => ({ id: i, left: Math.random() * 100, delay: Math.random() * 1.2, color: COLORS[i % COLORS.length] }));
  return <>{pieces.map((p) => <span key={p.id} className="confetti" style={{ left: `${p.left}%`, background: p.color, animationDelay: `${p.delay}s` }} />)}</>;
}

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { t } = useLang();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); refresh(); return; }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (attempts.current >= 10) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refresh]);

  const views = {
    checking: { icon: Hourglass, title: t("payChecking"), sub: "", tint: "bg-yuzu/60", spin: true },
    paid: { icon: PartyPopper, title: t("paySuccessTitle"), sub: t("paySuccessSub"), tint: "bg-matcha" },
    pending: { icon: Hourglass, title: t("payPendingTitle"), sub: t("payPendingSub"), tint: "bg-yuzu/60" },
    failed: { icon: XCircle, title: t("payFailedTitle"), sub: t("payFailedSub"), tint: "bg-coral/60" },
  };
  const v = views[state];
  const Icon = v.icon;

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-20">
      {state === "paid" && <Confetti />}
      <div className="kawaii-card p-10 text-center kawaii-pop" data-testid={`payment-${state}`}>
        <div className={`mx-auto w-20 h-20 rounded-full ${v.tint} flex items-center justify-center mb-6 ${v.spin ? "animate-pulse" : ""}`}>
          <Icon className="w-9 h-9 text-ink" strokeWidth={2.2} />
        </div>
        <h1 className="font-display text-3xl sm:text-4xl text-ink" data-testid="payment-title">{v.title}</h1>
        {v.sub && <p className="mt-3 text-ink-soft leading-relaxed">{v.sub}</p>}
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          {state === "paid" ? (
            <Link to="/pdf" data-testid="payment-go-sheets-btn" className="kawaii-btn kawaii-btn-primary">{t("goToSheets")} <ArrowRight className="w-4 h-4" strokeWidth={2.5} /></Link>
          ) : state === "failed" ? (
            <Link to="/pricing" data-testid="payment-back-pricing-btn" className="kawaii-btn kawaii-btn-ink">{t("backToPricing")}</Link>
          ) : null}
          <Link to="/" className="kawaii-btn">{t("backHome")}</Link>
        </div>
      </div>
    </div>
  );
}
